import React, { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import classNames from 'classnames';
import {
  getGradebook,
  getGradebookInstructor,
  updateShowPercentageCourseSettings,
} from '../../../../actions/gradebook';
import useSidePanel from '../../../SidePanel/hooks/useSidePanel';
import { togglePointOrGrade } from '../../../../actions/analyticsEvents';
import CustomMuiDropdown from '../../../CustomMuiDropdown';
import ManualAssignments from '../../../modals/ManualAssignments/ManualAssignments';
import MLIcon from '../../../MLIcon';
import { SIDE_PANEL_FRAME_ID, BLOCKS } from '../../../SidePanel/constants';
import { getConfigObject } from '../../../../utils/config';

import s from './OptionsMenu.module.css';

const BLOCK_NAME = BLOCKS.STUDENT_PERFORMANCE;

const MENU_ACTIONS = {
  SETTINGS: 'settings',
  ADD_ASSIGNMENT: 'add_assignment',
  EXPORT: 'export',
  SYNC_LMS: 'sync_lms',
  TOGGLE_PERCENT: 'toggle_percent',
  TOGGLE_V2: 'toggle_v2',
};

function OptionsMenu({
  dispatch,
  courseId,
  sendEvent,
  showSettings,
  showPercent,
  hasStudents,
  hasAssignments,
  disableEnforceFocus,
  isGatewayEnabled,
  isSectionManager,
  syncGradebook,
  toggleGradebookExportDialog,
  disableManualAssignmentsMenu,
  isGradebookV2Active,
  onToggleGradebookV2,
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    state: { mediaQueries },
  } = useSidePanel();

  const isMobile = useMediaQuery(`(max-width: ${mediaQueries.mobileOnly}px)`);

  const updatePerformanceBlock = () => {
    const sidePanelFrame = document.getElementById(SIDE_PANEL_FRAME_ID);
    if (sidePanelFrame && sidePanelFrame.contentWindow.frames[BLOCK_NAME]) {
      sidePanelFrame.contentWindow.frames[BLOCK_NAME].postMessage(
        { action: 'refresh', target: BLOCK_NAME },
        '*',
      );
    }
  };

  const handlePercentPointsToggle = async () => {
    await dispatch(updateShowPercentageCourseSettings(courseId, !showPercent));
    dispatch(
      sendEvent(togglePointOrGrade(courseId, !showPercent ? '%' : 'pts')),
    );
    if (isGradebookV2Active) {
      dispatch(getGradebookInstructor(courseId));
    } else {
      dispatch(getGradebook(courseId));
    }
    updatePerformanceBlock();
  };

  const buildItems = () => {
    const items = [
      {
        label: 'Gradebook Settings',
        value: MENU_ACTIONS.SETTINGS,
      },
    ];
    if (!isSectionManager) {
      items.push(
        ...[
          {
            disabled: disableManualAssignmentsMenu,
            label: 'Add Non-Achieve Assignment',
            value: MENU_ACTIONS.ADD_ASSIGNMENT,
            returnFocus: false,
          },
          {
            label: 'Export Gradebook',
            value: MENU_ACTIONS.EXPORT,
          },
        ],
      );
    }
    if (!isSectionManager && isGatewayEnabled) {
      items.push({
        label: 'Sync to LMS',
        value: MENU_ACTIONS.SYNC_LMS,
      });
    }
    if (
      !isSectionManager &&
      (hasStudents || (isGradebookV2Active && hasAssignments))
    ) {
      const label = `Switch to ${showPercent ? 'Points' : 'Percent'} View`;
      items.push({
        label,
        value: MENU_ACTIONS.TOGGLE_PERCENT,
      });
    }
    const { FF_IS_INSTRUCTOR_GRADEBOOK_V2_DEV_MODE_ENABLED = false } =
      getConfigObject();
    if (FF_IS_INSTRUCTOR_GRADEBOOK_V2_DEV_MODE_ENABLED) {
      items.push({
        label: 'Gradebook V2',
        value: MENU_ACTIONS.TOGGLE_V2,
        selected: isGradebookV2Active,
      });
    }
    return items;
  };

  const handleItemClick = (item) => {
    switch (item.value) {
      case MENU_ACTIONS.SETTINGS:
        showSettings();
        break;
      case MENU_ACTIONS.ADD_ASSIGNMENT:
        setShowCreateModal(true);
        break;
      case MENU_ACTIONS.EXPORT:
        toggleGradebookExportDialog();
        break;
      case MENU_ACTIONS.SYNC_LMS:
        syncGradebook();
        break;
      case MENU_ACTIONS.TOGGLE_PERCENT:
        handlePercentPointsToggle();
        break;
      case MENU_ACTIONS.TOGGLE_V2:
        onToggleGradebookV2();
        break;
      default:
        break;
    }
  };

  const items = buildItems();

  return (
    <>
      <CustomMuiDropdown
        isMenu
        items={items}
        title="Options"
        onClickItem={handleItemClick}
        dataTestId="gradebook-options-button"
        menuAlign="right"
        className={classNames(s.menuButton, {
          [s.menuButtonMedium]: isMobile,
        })}
        labelClassName={classNames({
          [s.buttonLabelMedium]: isMobile,
        })}
        optionsClassName={s.dropdownMenu}
        buttonLeftIcon={
          <MLIcon
            viewBox="0 0 18 18"
            type="dots_vertical"
            className={classNames(s.buttonLeftIcon, {
              [s.buttonLeftIconMedium]: isMobile,
            })}
          />
        }
        buttonRightIcon={
          <MLIcon
            className={classNames(s.buttonRightIcon, {
              [s.buttonRightIconMedium]: isMobile,
            })}
            type="caret_down"
          />
        }
      />
      {showCreateModal && (
        <ManualAssignments
          showModal={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
          }}
          disableEnforceFocus={disableEnforceFocus}
        />
      )}
    </>
  );
}

export default OptionsMenu;
